import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { AffiliateUser, CustomerUser, NavItem, User } from '../types/types';
import { useAuthManager } from '../hooks/useAuthManager';
import { useUserDataManager } from '../hooks/useUserDataManager';
import { useNavigationManager } from '../hooks/useNavigationManager';

type AuthType = 'customer' | 'partner';

export interface MyAccountContextType {
  affiliate: AffiliateUser | null;
  customer: CustomerUser | null;
  isPending: boolean;
  isUserAuthenticated: boolean;
  logout: () => void;
  navItems: NavItem[];
  setUser: (user: User) => void;
  user: User | null;
  currentAuthType: AuthType;
  loginAsCustomer: () => void;
  loginAsPartner: () => void;
  refetchUserData: () => void;
}

const MyAccountContext = createContext<MyAccountContextType | undefined>(
  undefined
);

export const useMyAccount = () => {
  const context = useContext(MyAccountContext);

  if (!context) {
    // Return default values
    const defaultContextValue: MyAccountContextType = {
      affiliate: null,
      customer: null,
      isPending: false,
      isUserAuthenticated: false,
      logout: () => {},
      navItems: [],
      setUser: () => {},
      user: null,
      currentAuthType: 'customer',
      loginAsCustomer: () => {},
      loginAsPartner: () => {},
      refetchUserData: () => {},
    };

    return defaultContextValue;
  }

  return context;
};

interface MyAccountProviderProps {
  children: ReactNode;
  customerInstance: PublicClientApplication;
  partnerInstance: PublicClientApplication;
}

/**
 * MyAccount Provider Component
 *
 * ========================================
 * COMPLETE AUTHENTICATION FLOW OVERVIEW
 * ========================================
 *
 * STEP 1: User clicks login button (Customer or Partner)
 * STEP 2: AuthManager.loginAsCustomer() or loginAsPartner() called
 * STEP 3: MSAL redirects to Azure AD login page
 * STEP 4: User authenticates with Azure AD (external)
 * STEP 5: Azure AD redirects back to our app with auth tokens
 * STEP 6: AuthManager.handleRedirects() processes the return
 * STEP 7: MSAL fires LOGIN_SUCCESS event
 * STEP 8: AuthManager extracts user data from token claims
 * STEP 9: AuthManager calls UserDataManager.setUser()
 * STEP 10: AuthManager navigates to dashboard
 * STEP 11: UserDataManager processes user state change:
 *   11A: Sets up API headers with auth tokens
 *   11B: Encrypts and stores user data in session storage
 *   11C: Updates user state (triggers data fetching)
 *   11D: Determines fetch strategy (customer vs affiliate)
 *   11E-F: For affiliates: Two-step API process to get complete data
 *   11G: For customers: Single API call to get user data
 * STEP 12: UserDataManager processes API responses:
 *   12A: Customer data processed and state updated
 *   12B: Affiliate data merged from two sources and state updated
 * STEP 13: NavigationManager sets appropriate navigation based on user type
 * STEP 14: NavigationManager sends analytics flags
 * STEP 15: Dashboard renders with complete user context and navigation
 *
 * Component Architecture:
 * - AuthManager: Handles MSAL and authentication state (steps 2-10)
 * - UserDataManager: Manages data fetching and user state (steps 11-12)
 * - NavigationManager: Sets navigation and sends analytics (steps 13-14)
 */
export const MyAccountProvider: React.FC<MyAccountProviderProps> = ({
  children,
  customerInstance,
  partnerInstance,
}) => {
  // Initialize user data management (handles steps 11-12)
  const { user, customer, affiliate, isPending, setUser, refetchUserData } =
    useUserDataManager();

  // Initialize authentication management (handles steps 2-10)
  const {
    currentAuthType,
    isUserAuthenticated,
    loginAsCustomer,
    loginAsPartner,
    logout,
  } = useAuthManager(customerInstance, partnerInstance, user, setUser);

  // Initialize navigation management (handles steps 13-14)
  const navItems = useNavigationManager(user, customer, affiliate);

  // STEP 15: Create context value with all processed data for dashboard consumption
  const contextValue = useMemo(
    () => ({
      affiliate,
      customer,
      isPending,
      isUserAuthenticated,
      logout,
      navItems,
      setUser,
      user,
      currentAuthType,
      loginAsCustomer,
      loginAsPartner,
      refetchUserData,
    }),
    [
      affiliate,
      customer,
      isPending,
      isUserAuthenticated,
      logout,
      navItems,
      setUser,
      user,
      currentAuthType,
      loginAsCustomer,
      loginAsPartner,
      refetchUserData,
    ]
  );

  return (
    <MyAccountContext.Provider value={contextValue}>
      {children}
    </MyAccountContext.Provider>
  );
};
